import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { Loader2, Trophy, XCircle, Users2, Target, TrendingUp, AlertTriangle, CalendarX, BellOff, Percent } from "lucide-react";
import api from "@/lib/api";
import { PRODUCT_TYPES } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["Full Year", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const cr = (v) => (Number(v || 0) / 1e7).toFixed(2);

function Panel({ title, icon: Icon, children, className = "", ...rest }) {
  return (
    <Card className={`p-4 ${className}`} {...rest}>
      <div className="flex items-center gap-1.5 mb-3">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <h3 className="font-heading font-semibold text-sm text-slate-800">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

function MiniBars({ data }) {
  return (
    <ResponsiveContainer width="100%" height={130}>
      <BarChart data={data} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 9, fill: "#cbd5e1" }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v) => `${v}`} cursor={{ fill: "#f8fafc" }} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Variance({ achieved, target }) {
  const diff = Number(achieved || 0) - Number(target || 0);
  const ok = diff >= 0;
  const pct = target ? Math.round((Number(achieved || 0) / Number(target)) * 100) : 0;
  return (
    <div className="flex items-center justify-between text-xs mt-1">
      <span className="text-slate-400">{pct}% of target</span>
      <span className={`font-semibold ${ok ? "text-emerald-600" : "text-orange-600"}`}>{ok ? "+" : ""}{cr(diff)}</span>
    </div>
  );
}

function Funnel({ stages }) {
  const palette = ["#231f20", "#0ea5e9", "#f59e0b", "#E2241B", "#10B981"];
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <div className="space-y-1.5">
      {stages.map((s, i) => {
        const w = Math.max(28, Math.round((s.count / max) * 100));
        return (
          <div key={s.label} className="flex items-center gap-2" data-testid={`funnel-${s.label.replace(/\s+/g, "-")}`}>
            <div className="h-7 rounded text-white text-xs font-medium flex items-center px-2.5 transition-all" style={{ width: `${w}%`, backgroundColor: palette[i % palette.length], minWidth: 80 }}>
              {s.label}
            </div>
            <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">{s.count}{s.value ? ` · ${cr(s.value)}Cr` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function WinLoss({ title, data }) {
  return (
    <Panel title={title} icon={Trophy}>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-emerald-50 p-3">
          <p className="text-[11px] text-emerald-700 font-semibold uppercase tracking-wide flex items-center gap-1"><Trophy className="h-3 w-3" /> Win</p>
          <p className="font-heading text-lg font-bold text-emerald-700 mt-1">{cr(data.win_value)}</p>
          <p className="text-xs text-emerald-600">{data.win_count} deals</p>
        </div>
        <div className="rounded-lg bg-rose-50 p-3">
          <p className="text-[11px] text-rose-700 font-semibold uppercase tracking-wide flex items-center gap-1"><XCircle className="h-3 w-3" /> Loss</p>
          <p className="font-heading text-lg font-bold text-rose-700 mt-1">{cr(data.loss_value)}</p>
          <p className="text-xs text-rose-600">{data.loss_count} deals</p>
        </div>
      </div>
    </Panel>
  );
}

function PotentialTile({ icon: Icon, label, count, value, tone }) {
  const tones = { red: "bg-rose-50 text-rose-700 border-rose-200", amber: "bg-amber-50 text-amber-700 border-amber-200", slate: "bg-slate-50 text-slate-700 border-slate-200" };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`} data-testid={`potential-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <Icon className="h-4 w-4 mb-1.5 opacity-70" />
      <p className="text-[11px] font-medium leading-tight">{label}</p>
      <p className="font-heading text-lg font-bold mt-1">{count}</p>
      <p className="text-[11px] opacity-80">{cr(value)}</p>
    </div>
  );
}

export function SalesCockpit() {
  const [f, setF] = useState({ fin_year: "", month: 0, zone: "", branch: "", sales_person: "", product_type: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v === "__all" ? (k === "month" ? 0 : "") : v }));
  const { data, isLoading } = useQuery({
    queryKey: ["cockpit", f],
    queryFn: async () => (await api.get("/sales-mis/cockpit", { params: { ...f, fin_year: f.fin_year || undefined } })).data,
    refetchInterval: 60000,
  });

  const flt = data?.filters || {};
  const Selector = ({ k, placeholder, options, width = 130 }) => (
    <Select value={String(f[k] || "__all")} onValueChange={(v) => set(k, v)}>
      <SelectTrigger className="h-9" style={{ width }} data-testid={`cockpit-filter-${k}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options}</SelectContent>
    </Select>
  );

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Select value={String(f.fin_year || (flt.fin_year ?? "__all"))} onValueChange={(v) => set("fin_year", v === "__all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[120px]" data-testid="cockpit-filter-fy"><SelectValue placeholder="Fin Year" /></SelectTrigger>
          <SelectContent>{(flt.fin_years || []).map((y) => <SelectItem key={y} value={String(y)}>FY {y}-{String((y + 1) % 100).padStart(2, "0")}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(f.month)} onValueChange={(v) => setF((p) => ({ ...p, month: Number(v) }))}>
          <SelectTrigger className="h-9 w-[120px]" data-testid="cockpit-filter-month"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Selector k="zone" placeholder="Zone" options={<><SelectItem value="__all">All Zones</SelectItem>{(flt.zones || []).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</>} />
        <Selector k="branch" placeholder="Branch" options={<><SelectItem value="__all">All Branches</SelectItem>{(flt.branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</>} />
        <Selector k="sales_person" placeholder="Sales Person" width={160} options={<><SelectItem value="__all">All Sales People</SelectItem>{(flt.sales_people || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</>} />
        <Selector k="product_type" placeholder="Type of Product" width={160} options={<><SelectItem value="__all">All Product Types</SelectItem>{PRODUCT_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</>} />
      </div>

      {isLoading || !data ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Panel title="Booking — Target vs Achieved" icon={Target} data-testid="cockpit-booking">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Fin Year (INR Cr)</p>
              <MiniBars data={[{ name: "Target", value: Number(cr(data.booking.target_fy)), color: "#cbd5e1" }, { name: "Achieved", value: Number(cr(data.booking.achieved_fy)), color: data.booking.achieved_fy >= data.booking.target_fy ? "#10B981" : "#E2241B" }]} />
              <Variance achieved={data.booking.achieved_fy} target={data.booking.target_fy} />
              {f.month > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mt-3 mb-1">{MONTHS[f.month]} (INR Cr)</p>
                  <MiniBars data={[{ name: "Target", value: Number(cr(data.booking.target_month)), color: "#cbd5e1" }, { name: "Plan", value: Number(cr(data.booking.plan_month)), color: "#0ea5e9" }, { name: "Actual", value: Number(cr(data.booking.achieved_month)), color: "#10B981" }]} />
                </>
              )}
            </Panel>

            <Panel title="Billing — Target vs Achieved" icon={TrendingUp} data-testid="cockpit-billing">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Fin Year (INR Cr) · YTD snapshot</p>
              <MiniBars data={[{ name: "Target", value: Number(cr(data.billing.target_fy)), color: "#cbd5e1" }, { name: "Achieved", value: Number(cr(data.billing.achieved_fy)), color: data.billing.achieved_fy >= data.billing.target_fy ? "#10B981" : "#E2241B" }]} />
              <Variance achieved={data.billing.achieved_fy} target={data.billing.target_fy} />
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                <Percent className="h-4 w-4 text-slate-400" />
                <p className="text-xs text-slate-500"><span className="font-semibold text-slate-700">{data.billing.avg_discount}%</span> avg quote discount</p>
              </div>
            </Panel>

            <Panel title={`Pipeline · ${cr(data.pipeline.value)}`} icon={Users2} data-testid="cockpit-pipeline">
              <Funnel stages={data.pipeline.stages} />
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <WinLoss title="Win / Loss — Fin Year" data={data.win_loss.fy} />
            <WinLoss title={`Win / Loss — ${f.month > 0 ? MONTHS[f.month] : "This Month"}`} data={data.win_loss.month} />
            <Panel title="Contacts" icon={Users2}>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-blue-50 p-3"><p className="text-[11px] text-blue-700 uppercase font-semibold">Fin Year</p><p className="font-heading text-2xl font-bold text-blue-700 mt-1">{data.contacts.fy}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] text-slate-500 uppercase font-semibold">Month</p><p className="font-heading text-2xl font-bold text-slate-700 mt-1">{data.contacts.month}</p></div>
              </div>
            </Panel>
          </div>

          <Panel title="Potential Status (open deals)" icon={AlertTriangle}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <PotentialTile icon={AlertTriangle} label="> 90 days old" count={data.potential.gt90.count} value={data.potential.gt90.value} tone="red" />
              <PotentialTile icon={AlertTriangle} label="> 30 days old" count={data.potential.gt30.count} value={data.potential.gt30.value} tone="amber" />
              <PotentialTile icon={CalendarX} label="Past closing date" count={data.potential.past_close.count} value={data.potential.past_close.value} tone="slate" />
              <PotentialTile icon={BellOff} label="No follow-up set" count={data.potential.no_followup.count} value={data.potential.no_followup.value} tone="slate" />
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
